import { useTranslation } from 'react-i18next';
import { Typography, Box, IconButton } from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import UserListSummaryFilterSection from './components/PreAdmissionSummaryFilterSection';
import AppDataGridHeaderWithSorting from '@/common/components/dataGrid/AppDataGridHeaderWithSorting';
import { PermissionEnum, PreAdmissionTab } from '@/api/enum/common.enum';
import { useGetPreAdmissionListQuery } from '@/api/pre-admission/preAdmission';
import {
	PreAdmissionFilter,
	PreAdmissionPageDTO,
} from '@/api/DTO/pre-admission/preAdmission.interface';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useSelector } from 'react-redux';
import PreAdmissionChip from '@/common/components/PreAdmissionChip';
import { useGetStaffAllListQuery } from '@/api/common';
import { useGetUser } from '@/hooks/useGetUser';

interface Props {
	preAdmissionTab: PreAdmissionTab;
}

export default function PreAdmissionSummaryPage(props: Props) {
	const { preAdmissionTab } = props;
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const userInfoDetail = useSelector(selectMyInfo);
	const { userInfo } = useGetUser();

	const defaultPropertyFilter: PreAdmissionFilter = {
		pagination: initialPagination,
		searchString: '',
		approachSourceList: [],
		sortings: [],
		createdByList: [],
		preAdmissionStatus: preAdmissionTab,
		branchId: userInfoDetail?.currentBranch?.id,
	};
	const defaultValues: DefaultValues<PreAdmissionFilter> =
		defaultPropertyFilter;
	const methods = useForm<PreAdmissionFilter>({
		defaultValues,
	});
	const transformedSearchParams: PreAdmissionFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'searchString',
			'preAdmissionStatus',
			'createdByList',
			'approachSourceList',
			'branchId',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetPreAdmissionListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
			skip: !userInfoDetail?.currentBranch,
		});

	const transformResponse = useCallback((response?: PreAdmissionPageDTO[]) => {
		if (response == null) return;
		const newResponse: any[] | undefined = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				enquiryDate: !item.enquiryDate
					? '-'
					: dayjs(item.enquiryDate).locale('en').format(DISPLAY_DATE_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as PreAdmissionFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const DataGridHeader = (params: GridColumnHeaderParams) => {
		return (
			<AppDataGridHeaderWithSorting
				headerName={params.colDef.headerName}
				sortingsPropertyName={params.colDef.field}
			/>
		);
	};

	const columns: GridColDef[] = [
		{
			field: 'preAdmissionNo',
			headerName: t('preAdmission.preAdmissionNumber'),
			flex: 15,
		},
		{
			field: 'fileNo',
			headerName: t('preAdmission.fileNumber'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('preAdmission.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography
						sx={{ display: 'block' }}
						fontSize='body2'
						variant='regular'
					>
						{params.row.nameEn}
					</Typography>
					<Typography fontSize='body2' variant='regular'>
						{params.row.nameZh}
					</Typography>
				</Box>
			),
		},
		{
			field: 'enquiryDate',
			headerName: t('preAdmission.enquiryDate'),
			flex: 15,
		},
		{
			field: 'approachSourceDesc',
			headerName: t('preAdmission.approachAource'),
			flex: 15,
		},
		{
			field: 'preAdmissionStatusDesc',
			headerName: t('preAdmission.preAdmissionStatus'),
			flex: 15,
			maxWidth: 160,
			renderCell: (params) => (
				<>
					<PreAdmissionChip
						status={params.row.preAdmissionStatus}
						label={params.row.preAdmissionStatusDesc}
					/>
				</>
			),
		},
		{
			field: 'createdBy',
			headerName: t('preAdmission.creator'),
			flex: 15,
		},

		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				userInfo?.permissionList?.includes(PermissionEnum.ViewPreAdmission) && (
					<Link
						to={`/pre-admission/pre-admission-list/detail/${params.id}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				),
		},
	];

	return (
		<>
			<FormProvider {...methods}>
				<UserListSummaryFilterSection></UserListSummaryFilterSection>

				<Box marginTop={2}>
					<AppDataGrid
						loading={!isGetPropertySummarySuccess}
						rows={transformResponse(propertySummary?.data?.items) ?? []}
						rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
						columns={columns}
						getRowId={(row) => row.id}
					/>
				</Box>
			</FormProvider>
		</>
	);
}
